import { Location } from '@angular/common';
import { Component, Injector, OnDestroy, OnInit, effect, resolveForwardRef } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { DroppedWidgetIntoLayoutWidgetAction } from '@app/finxone-web-frontend/app/lib/actions/dropped.action';
import { SelectWidget } from '@app/finxone-web-frontend/app/lib/actions/grid.action';
import {
  CloseModalPopupAction,
  UpdatePageConfigStyle,
} from '@app/finxone-web-frontend/app/lib/actions/page-context.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { HideConditionsService } from '@app/finxone-web-frontend/app/lib/services/hide-conditions-service/hide-conditions.service';
import { MultiPartFormHolderService } from '@app/finxone-web-frontend/app/lib/services/multipart-holder/multipart-form-holder.service';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import { RoleState } from '@app/finxone-web-frontend/app/lib/state/role.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { getComponentByLabel } from '@app/finxone-web-frontend/app/lib/utils/get-component.utils';
import { buildHandleBarModel } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { getWidgetClasses } from '@app/finxone-web-frontend/app/widget.utis';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  NewUiZoneWidgetConfig,
  ProfileStateModel,
  UiPageConfig,
  UserInfoType,
  WidgetNames,
  isLayoutWidget,
} from '@finxone-platform/shared/sys-config-types';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import {
  Observable,
  Subject,
  delay,
  distinctUntilChanged,
  filter,
  map,
  of,
  skip,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  applyPageConfigStyles,
  createInjector,
  fetchProfileAndSetOrgInfoBasedOnZone,
  getRouteParams,
  hideWidgetsIfNeededWithCallback,
  isBodyScrollingEnabled,
  resetStylesOnWebApp,
} from '../../utils';
import { ZonePageUrlProvider } from '../../zone-url-provider';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-main-widget',
  templateUrl: 'main-widget.component.html',
})
export class MainWidgetComponent implements OnInit, OnDestroy {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  readonly accordionWidgetName = WidgetNames.ACCORDION_WIDGET;
  readonly indexedLayoutWidgetNames = new Set<string>([WidgetNames.ACCORDION_WIDGET]);
  urlParams: {
    zoneUrlInput: string;
    pageUrlInput: string;
    roleInput: string;
    subscriptionInput: string;
    idInput: string;
  };
  isWorkflowType: boolean;
  currentPageDetail: UiPageConfig;
  widgetLayout: NewUiZoneWidgetConfig[] = [];

  userInfo: UserInfoType;

  @Select(ProfileState.getProfile)
  profile$!: Observable<ProfileStateModel>;
  profile: ProfileStateModel;

  injectorComponents: {
    [key: string]: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      component: any;
      injector: Injector;
      widget: NewUiZoneWidgetConfig;
    };
  } = {};
  private isModalPage: boolean;
  private pageIndex: number | undefined;
  private lastVisibleSignature = '';

  get injectorArray() {
    return Object.values(this.injectorComponents);
  }

  constructor(
    public configService: ConfigService,
    private injector: Injector,
    private route: ActivatedRoute,
    private store: Store,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private formHolderService: MultiPartFormHolderService,
    private location: Location,
    private actions: Actions,
    private hideConditionsService: HideConditionsService,
  ) {
    effect(() => {
      const pageHideConditions = this.hideConditionsService.currentPageHideConditions();
      const hideConditionFromWidget = this.hideConditionsService.hideConditionFromWidget();
      const currentModelToCheck = this.hideConditionsService.currentModelToCheck();
      if (currentModelToCheck && pageHideConditions.length > 0 && hideConditionFromWidget.length) {
        const shouldReRender = this.hideConditionsService.shouldReRenderPageWidgets();
        if (shouldReRender) {
          this.hideConditionsService.resetHideConditionFromWidget();
          this.hideConditionsService.resetCurrentPageHideConditions();
          this.getLayout();
        }
      }
    });

    /**
     * Reactively re-evaluate old-style `hideCondition`s when the handlebar model changes.
     *
     * The model is pushed into `currentModelToCheck` by the rendered handlebar widgets (e.g. a
     * balance text widget) as NGXS state changes - so after a savings withdrawal refreshes
     * SavingsAccountState, this effect re-runs with a fresh model and a widget whose visibility
     * depends on that state (e.g. a withdrawal-count) is shown/hidden, even while the page sits
     * behind an open modal.
     *
     * IMPORTANT: this reuses the already-built model and NEVER calls getLayout()/buildHandleBarModel.
     * buildHandleBarModel has refresh side effects (it dispatches GetAccountRequests, GetAccount,
     * etc. on every invocation), so re-invoking it reactively caused a refresh -> state change ->
     * re-invoke API loop. Here we only re-filter (pure) and, gated by an actual change to the set of
     * VISIBLE widgets, rebuild the affected widgets - so a pure data change is a no-op and cannot loop.
     */
    effect(() => {
      const model = this.hideConditionsService.currentModelToCheck();
      if (!model || Object.keys(model).length === 0 || !this.widgetLayout?.length) {
        return;
      }
      hideWidgetsIfNeededWithCallback(model, this.widgetLayout, () => {
        const visibleSignature = this.computeVisibleSignature(this.widgetLayout);
        if (visibleSignature === this.lastVisibleSignature) {
          return;
        }
        this.lastVisibleSignature = visibleSignature;
        this.processWidgetLayoutUpdates();
      });
    });

    resetStylesOnWebApp();
    getRouteParams(this.route)
      .pipe(
        switchMap((result) => {
          this.urlParams = result;
          return fetchProfileAndSetOrgInfoBasedOnZone(this.profile$, this.store, this.urlParams.zoneUrlInput);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((profile) => {
        if (profile) {
          this.profile = profile;
          // Populate userInfo too — dataIn.userProfileData reads it, and it was never assigned.
          this.userInfo = profile as unknown as UserInfoType;
        }
      });

    this.isWorkflowType = this.configService.getActiveConfigType()?.isWorkflow;

    // Ensure clean initialization state for multi-part forms
    this.formHolderService.setFormStages([]);
  }

  mouseOver = '';
  mouseOverId = '';
  changeStyle($event: MouseEvent, mouseOverId?: string) {
    this.mouseOver = $event.type == 'mouseover' ? 'widget-hover' : '';
    if (mouseOverId) this.mouseOverId = mouseOverId;
  }
  selectWidget(event: MouseEvent, widget: NewUiZoneWidgetConfig) {
    if (typeof widget.uniqueId === 'number') {
      widget.uniqueId = widget.uniqueId.toString();
    }

    const uniqueId = widget.uniqueId;
    if (!uniqueId) {
      console.error('Widget uniqueId is undefined or null');
      return;
    }

    if (this.isWorkflowType) {
      if (event.shiftKey) {
        this.store.dispatch(new SelectWidget(uniqueId, true));
      } else {
        this.store.dispatch(new SelectWidget(uniqueId, false));
      }
    }
    event.stopPropagation();
  }

  getWidgetClasses = getWidgetClasses;

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        distinctUntilChanged(),
        switchMap((params: ParamMap) => {
          this.parseRouteParameters(params);
          this.validateRouteParameters();

          return this.getPageIndexAndInitialize();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    if (this.urlParams.zoneUrlInput === APP_ZONES.SIGNED_OUT_FIRST_INTRODUCTION) {
      localStorage.setItem('firstTime', 'true');
    }

    this.getLayout();
    this.onGetCloseModalPopupEvent();
    this.onUpdatePageConfigStyle();
    this.onRoleChange();
  }

  /**
   * Rebuild the page layout when the persona changes without a page reload.
   *
   * Only rebuilds when the current page exists for the new persona. Rebuilding
   * blindly falls back to page index 0, and an empty layout triggers
   * location.back() — which can cascade through history into feature-redirect
   * and its 4s reload timer (infinite reload loop).
   */
  private onRoleChange(): void {
    this.store
      .select(RoleState.getRole)
      .pipe(
        map((role) => role.role),
        filter((role): role is string => !!role),
        distinctUntilChanged(),
        skip(1), // initial layout is handled by the route subscription above
        switchMap(() =>
          this.configService
            .getPageUrlPageIndex(this.urlParams.zoneUrlInput, this.urlParams.pageUrlInput)
            .pipe(take(1)),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((pageIndex) => {
        if (pageIndex !== undefined && pageIndex !== -1) {
          this.pageIndex = pageIndex;
          this.getLayout(true);
        } else {
          console.warn(
            `Page ${this.urlParams.pageUrlInput} does not exist for the new persona; keeping current view`,
          );
        }
      });
  }

  private parseRouteParameters(params: ParamMap): void {
    this.isModalPage = !!params.get('modal-page');
  }

  private validateRouteParameters(): void {
    if (!this.urlParams.zoneUrlInput) {
      throw new Error('No zone specified via path or input');
    }
    if (!this.urlParams.pageUrlInput) {
      throw new Error('No page specified via path or input');
    }
  }

  private getPageIndexAndInitialize(): Observable<unknown> {
    return this.configService
      .getPageUrlPageIndex(this.urlParams.zoneUrlInput, this.urlParams.pageUrlInput)
      .pipe(
        take(1),
        switchMap((pageIndex) => {
          this.pageIndex = pageIndex;
          return this.handlePageInitialization();
        }),
      );
  }

  private handlePageInitialization(): Observable<unknown> {
    const id = this.route?.snapshot?.paramMap?.get('id');

    if (id == null && this.urlParams.zoneUrlInput === formatNameAsUrl(APP_ZONES.VERIFICATION)) {
      return this.profile$.pipe(
        take(1),
        tap(() => this.getLayout()),
      );
    } else {
      this.getLayout();
      return of(null);
    }
  }

  /**
   * When modal popup is closed, this function will get called and get the current page config
   */
  private onGetCloseModalPopupEvent() {
    this.actions
      .pipe(ofActionDispatched(CloseModalPopupAction), delay(100), takeUntil(this.destroy$))
      .subscribe((action) => {
        const pageUrlInput = action.isModal
          ? this.route.snapshot.params?.['modal-page']
          : this.route.snapshot.params?.['page'];
        this.urlParams.zoneUrlInput = this.route.snapshot.params?.['zone'] ?? this.urlParams.zoneUrlInput;
        if (pageUrlInput) {
          this.urlParams.pageUrlInput = pageUrlInput;
          this.getLayout();
        }
        this.getCurrentPageConfigByPageUrl();
      });
  }

  /**
   * This action needs to be called when navigation happens from modal to page for update the page config style
   */
  private onUpdatePageConfigStyle() {
    this.actions
      .pipe(ofActionDispatched(UpdatePageConfigStyle), delay(100), takeUntil(this.destroy$))
      .subscribe(() => {
        this.getCurrentPageConfigByPageUrl();
      });
  }

  private getCurrentPageConfigByPageUrl(): void {
    const deviceScrollbarVisibility = this.store.selectSnapshot(
      ProjectSettingsState.getProjectSettings,
    )?.deviceScrollbarVisibility;
    this.configService
      .getPageUrlPage(this.urlParams.pageUrlInput)
      .pipe(take(1))
      .subscribe((currentPageDetail) => {
        if (currentPageDetail) {
          this.currentPageDetail = currentPageDetail;

          if (currentPageDetail.pageConfiguration) {
            isBodyScrollingEnabled(
              this.configService.getDeviceData().deviceType,
              currentPageDetail,
              deviceScrollbarVisibility,
            );

            const webAppElement = document.getElementById('web-app');
            if (webAppElement) {
              applyPageConfigStyles(webAppElement, currentPageDetail, this.isModalPage, this.isWorkflowType);
            }
          }
        }
      });
  }

  private getLayout(fromRoleChange = false) {
    this.getCurrentPageConfigByPageUrl();
    this.configService
      .getCurrentRoleZoneWidgetsConfig(undefined, this.pageIndex)
      .pipe(
        distinctUntilChanged(),
        tap((res) => {
          this.widgetLayout = cloneDeep(res?.widgetLayouts);
          /**
           * when re-order the widget from widget tree, it refresh the widget by remove and adding new widgets.
           * The destroyAllComponents function needs to be executed before the if condition. Currently, when the last widget is removed, the container isn't cleared until a page refresh. Placing destroyAllComponents beforehand will ensure the container is wiped out as soon as the widget list becomes empty.
           */
          this.destroyAllComponents();
          this.injectorComponents = {};
        }),
        switchMap(() => {
          if (!this.widgetLayout?.length) {
            // No widgets on this page so navigate back — but never for a persona-switch
            // rebuild: back() can cascade through history into feature-redirect's reload loop
            if (!this.isWorkflowType && !fromRoleChange) {
              console.error('No widgets on page: ', this.currentPageDetail.url);
              this.location.back();
            }
            return of(null);
          }
          this.hideConditionsService.extractAndSetCurrentPageWidgetsHideConditions(this.widgetLayout);
          // One-time model build for the initial layout. Ongoing re-evaluation as state changes is
          // handled by the reactive `currentModelToCheck` effect (see constructor) - which reuses the
          // model the widgets compute and never re-invokes the side-effectful buildHandleBarModel.
          return buildHandleBarModel(
            this.store,
            '',
            this.countriesCurrenciesService,
            this.iconCacheService,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            this.configService,
          ).pipe(take(1));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((modelToEvaluateAgainst) => {
        if (modelToEvaluateAgainst) {
          this.hideConditionsService.currentModelToCheck.set(modelToEvaluateAgainst);
          hideWidgetsIfNeededWithCallback(modelToEvaluateAgainst, this.widgetLayout, () => {
            this.processWidgetLayoutUpdates();
            // Record the visible set so the reactive effect treats this initial build as the
            // baseline and only re-renders on a subsequent genuine visibility change.
            this.lastVisibleSignature = this.computeVisibleSignature(this.widgetLayout);
          });
        }
      });
  }

  /**
   * Builds a stable signature of the currently-visible widget tree (widget ids + nesting order).
   * A hide condition flipping a widget in/out changes this signature; a pure data change does not.
   * Used to decide whether a reactive re-evaluation needs to rebuild the rendered widgets at all.
   */
  private computeVisibleSignature(widgets: NewUiZoneWidgetConfig[]): string {
    const ids: string[] = [];
    const walk = (list: NewUiZoneWidgetConfig[]) => {
      for (const widget of list) {
        if (!widget) continue;
        ids.push(`${widget.uniqueId}`);
        const children = widget.childComponents ?? [];
        for (const child of children) {
          if (Array.isArray(child)) {
            walk(child);
          } else if (child) {
            walk([child]);
          }
        }
      }
    };
    walk(widgets);
    return ids.join('|');
  }

  /**
   * Reconcile the rendered widgets against the (possibly hide-condition-filtered) widget layout.
   *
   * Rebuilds the injector map in widget-layout order while PRESERVING already-created component
   * instances. Because the handlebar model is now reactive, this method can run repeatedly as
   * state changes; preserving existing injectors means only widgets whose visibility actually
   * changed are created/destroyed. Unaffected widgets keep the same component + injector
   * references, so `*ngComponentOutlet` does not recreate them (and they do not re-subscribe,
   * avoiding a re-render feedback loop).
   */
  private processWidgetLayoutUpdates(): void {
    const previous = this.injectorComponents;
    const next: typeof this.injectorComponents = {};
    const reusedKeys = new Set<string>();

    for (const widget of this.widgetLayout) {
      const unique = widget.uniqueId.toString();
      const existing = previous[unique];

      /**
       * Reuse the existing instance ONLY when the widget config is structurally unchanged.
       *
       * `filterHiddenWidgets` returns a fresh `{ ...widget, childComponents }` each evaluation, so a
       * layout/container widget whose CHILD was shown/hidden by a hide condition arrives here with a
       * different `childComponents` array. Its injector captured the original children via
       * `ChildComponentList` at creation time, so it must be recreated for a nested hide-condition
       * change to take effect. Comparing the serialized config keeps unaffected widgets (including
       * reactive leaf widgets like balances) intact while recreating only those that actually changed.
       */
      if (existing && JSON.stringify(existing.widget) === JSON.stringify(widget)) {
        next[unique] = existing;
        reusedKeys.add(unique);
        continue;
      }

      const component = resolveForwardRef(getComponentByLabel(widget.name));
      // Skip widgets with unmapped or unavailable component types to allow partial page rendering
      if (component) {
        next[unique] = {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          component: component,
          injector: createInjector(
            widget,
            this.currentPageDetail,
            this.injector,
            this.configService,
            new ZonePageUrlProvider(
              this.urlParams.zoneUrlInput,
              this.urlParams.pageUrlInput,
              this.urlParams.roleInput,
              this.urlParams.subscriptionInput,
              this.urlParams.idInput,
            ),
            this.userInfo,
            this.profile,
            undefined,
            isLayoutWidget(widget.name)
              ? [
                  {
                    provide: ChildComponentList,
                    useValue: new ChildComponentList(widget.childComponents ?? []),
                  },
                ]
              : [],
          ),
          widget: widget,
        };
      }
    }

    // Tear down components that were not reused - newly hidden widgets, and stale instances of
    // widgets that were recreated because their config (e.g. filtered children) changed.
    Object.keys(previous).forEach((key) => {
      if (!reusedKeys.has(key) && previous[key]?.component?.destroy) {
        previous[key].component.destroy();
      }
    });

    this.injectorComponents = next;
  }

  public drop(widgetDroppedInto: NewUiZoneWidgetConfig, event?: Event) {
    if (this.indexedLayoutWidgetNames.has(widgetDroppedInto.name)) {
      const targetIndex = this.getIndexedLayoutDropIndex(event, widgetDroppedInto.uniqueId);
      this.store.dispatch(new DroppedWidgetIntoLayoutWidgetAction(widgetDroppedInto, undefined, targetIndex));
      event?.stopPropagation();
      return;
    }

    return this.store.dispatch(new DroppedWidgetIntoLayoutWidgetAction(widgetDroppedInto));
  }

  /**
   * Determines the drop index for widgets that support indexed layouts (like Accordion and Tab widgets) by analyzing the event's target and its ancestors for specific data attributes. This allows for accurate placement of the dropped widget within the layout, even when the drop event's target is a nested element rather than the layout container itself.
   */
  private getIndexedLayoutDropIndex(event: Event | undefined, uniqueId: string | number): number {
    const uniqueIdStr = uniqueId.toString();
    const probeElements = this.getDropProbeElements(event);

    if (!probeElements.length) {
      return 0;
    }

    for (const probe of probeElements) {
      const dataContainer = probe.closest('[data-layout-parent-id][data-layout-index]');
      if (dataContainer instanceof HTMLElement) {
        const parentId = dataContainer.dataset?.['layoutParentId'];
        const rawIndex = dataContainer.dataset?.['layoutIndex'];

        if (parentId === uniqueIdStr && rawIndex !== undefined) {
          const parsed = Number.parseInt(rawIndex, 10);
          if (!Number.isNaN(parsed)) {
            return parsed;
          }
        }
      }
    }

    return 0;
  }

  /**
   * Gathers a list of elements to probe for determining the drop index when a widget is dropped into an indexed layout widget. It includes the event target, its composed path, and elements at the event's client coordinates to ensure that even if the drop event's target is a nested element, the correct layout container can be identified for accurate widget placement.
   */
  private getDropProbeElements(event: Event | undefined): Element[] {
    const probes: Element[] = [];

    if (event?.target instanceof Element) {
      probes.push(event.target);
    }

    const path = event?.composedPath?.() ?? [];
    for (const node of path) {
      if (node instanceof Element) {
        probes.push(node);
      }
    }

    if (event instanceof DragEvent && event.clientX >= 0 && event.clientY >= 0) {
      const pointElements = document.elementsFromPoint(event.clientX, event.clientY);
      probes.push(...pointElements);
    }

    return probes;
  }

  trackByWidgetLayout(index: number, component: { widget: NewUiZoneWidgetConfig }): string {
    return component.widget.uniqueId.toString();
  }

  private destroyAllComponents() {
    Object.values(this.injectorComponents).forEach((componentRef) => {
      if (componentRef && componentRef.component.destroy) {
        componentRef.component.destroy();
      }
    });
  }

  ngOnDestroy() {
    this.destroyAllComponents();
  }
}

export class ChildComponentList {
  constructor(public list: NewUiZoneWidgetConfig[] | NewUiZoneWidgetConfig[][]) {}
}
